import { getCiphersuiteFromName, getCiphersuiteImpl } from 'ts-mls';
/**
 * Cipher Suite und Envelope-Konstanten für Zone A.
 *
 * ## Post-Quanten, hybrid (seit Phase 19)
 *
 * `MLS_256_XWING_AES256GCM_SHA512_MLDSA87`. Die vier Teile:
 *
 *  - **X-Wing** als Schlüsselaustausch — ein Hybrid aus dem klassischen X25519
 *    UND ML-KEM-768 (Kyber, FIPS 203). Er fällt erst, wenn BEIDE gebrochen
 *    sind: Ein Quantencomputer allein reicht nicht (ML-KEM hält), ein
 *    unentdeckter Fehler in ML-KEM allein auch nicht (X25519 hält). Genau der
 *    Schutz gegen „heute abfangen, in Jahren mit einem Quantencomputer
 *    entschlüsseln".
 *  - **ML-DSA-87** (Dilithium, FIPS 204) als Signatur statt Ed25519 — auch die
 *    Echtheit der Schlüssel muss quantensicher sein, sonst unterschiebt ein
 *    Quantenangreifer einfach eigene.
 *  - **AES-256-GCM** statt AES-128 — gegen Grovers Algorithmus zählt die halbe
 *    Bitlänge, also bleiben 128 Bit statt 64.
 *  - **SHA-512** entsprechend.
 *
 * ## Der Preis, den das kostet (Anf. 66)
 *
 * Die Schlüssel und Signaturen sind groß: ein ML-KEM-Schlüssel misst rund
 * 1,2 kB statt 32 Byte, eine ML-DSA-Signatur rund 4,6 kB statt 64 Byte. Jedes
 * KeyPackage, jedes Welcome und jeder Commit wird dadurch spürbar größer — auf
 * einer Mobilverbindung mehr Daten je Beitritt und Schlüsselwechsel. Die
 * Rechenzeit ist dagegen unkritisch (ML-KEM ist schnell, gemessen im
 * einstelligen Millisekundenbereich). Weil MLS die teuren Schritte selten tut
 * — nicht je Nachricht, sondern bei Beitritt und Wechsel — ist der Tausch für
 * einen Familienchat vertretbar.
 *
 * ## Zwei zusätzliche Abhängigkeiten
 *
 * `@noble/post-quantum` (ML-KEM, ML-DSA) und `@hpke/hybridkem-x-wing` sind bei
 * `ts-mls` optional und werden nur für diese Suite gebraucht. Ohne sie wirft
 * die Bibliothek beim Aufbau — kein stiller Rückfall auf etwas Schwächeres.
 *
 * Die alte Wahl und ihre Begründung stehen weiter in
 * [ADR-003a](../../../docs/adr/ADR-003a-mls-implementierung.md); der Wechsel
 * ist in [ADR-003b](../../../docs/adr/ADR-003b-post-quanten.md) festgehalten.
 */
export const SUITE_NAME = 'MLS_256_XWING_AES256GCM_SHA512_MLDSA87';
let cached = null;
/** Einmal aufbauen und wiederverwenden — die Initialisierung ist nicht billig. */
export function suite() {
    cached ??= getCiphersuiteImpl(getCiphersuiteFromName(SUITE_NAME));
    return cached;
}
/**
 * Rückgabetypen bewusst nicht annotiert: TextEncoder liefert
 * `Uint8Array<ArrayBuffer>`, eine eigene `Uint8Array`-Annotation wäre breiter
 * (`ArrayBufferLike`) und passte dann nicht mehr zu `BufferSource` — WebCrypto
 * verlangt genau den engeren Typ.
 */
export const utf8 = {
    encode: (s) => new TextEncoder().encode(s),
    decode: (b) => new TextDecoder().decode(b),
};
export const b64 = {
    encode(bytes) {
        let s = '';
        for (const byte of bytes)
            s += String.fromCharCode(byte);
        return btoa(s);
    },
    decode(text) {
        const roh = atob(text);
        const out = new Uint8Array(roh.length);
        for (let i = 0; i < roh.length; i++)
            out[i] = roh.charCodeAt(i);
        return out;
    },
};
/**
 * Macht aus einem `Uint8Array` einen Puffer, den WebCrypto akzeptiert.
 *
 * Hintergrund: Seit TypeScript 5.7 ist `Uint8Array` über den Puffertyp
 * generisch. Eine Sicht auf einen `SharedArrayBuffer` erfüllt `BufferSource`
 * nicht — deshalb lehnt der Compiler jeden Wert ab, dessen Herkunft er nicht
 * kennt. Die Kopie kostet bei Schlüsselmaterial nichts und ist ehrlicher als
 * ein Cast, der das Problem nur verdeckt.
 */
export function alsPuffer(bytes) {
    const out = new Uint8Array(bytes.length);
    out.set(bytes);
    return out;
}
//# sourceMappingURL=suite.js.map